import { useSearchParams } from 'react-router-dom';

import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';

import { AppsTab } from './AppsTab';
import { IntegrationsTab } from './IntegrationsTab';
import { GroupsTab } from './GroupsTab';
import { LogTab } from './LogTab';
import { OverviewTab } from './OverviewTab';
import { PeopleTab } from './PeopleTab';
import { RequestsTab } from './RequestsTab';

export const PERMISSION_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'people', label: 'People' },
  { id: 'groups', label: 'Groups' },
  { id: 'apps', label: 'Apps' },
  { id: 'requests', label: 'Requests' },
  { id: 'log', label: 'Log' },
] as const;
export type PermissionTab = (typeof PERMISSION_TABS)[number]['id'];

/** The tab of `?tab=`; an unknown or missing value opens the first tab. */
export function tabOf(value: string | null): PermissionTab {
  return PERMISSION_TABS.find((tab) => tab.id === value)?.id ?? 'overview';
}

/**
 * Studio Admin, Permissions for RapidSOS: who holds which access, who asked, and what the service refused. Every tab
 * opens on a list. A row opens one object, named in the URL (`?tab=people&open=ada@rapidsos.com`), so a link goes back to it.
 */
export function PermissionsPage() {
  const [params, setParams] = useSearchParams();
  const tab = tabOf(params.get('tab'));
  const open = params.get('open') ?? undefined;
  const go = (next: PermissionTab, id?: string) => setParams(id ? { tab: next, open: id } : { tab: next });
  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader title="Permissions" description="Who holds which access in RapidSOS, who asked for more, and what the service refused." />
      <div className="mb-6">
        <SegmentedFilter<PermissionTab> label="Permissions section" options={[...PERMISSION_TABS]} value={tab} onChange={(next) => go(next)} />
      </div>
      {tab === 'overview' ? <OverviewTab onGo={(next) => go(next)} /> : null}
      {tab === 'integrations' ? <IntegrationsTab open={open} onOpen={(id) => go('integrations', id)} /> : null}
      {tab === 'people' ? <PeopleTab open={open} onOpen={(id) => go('people', id)} /> : null}
      {tab === 'groups' ? <GroupsTab open={open} onOpen={(id) => go('groups', id)} /> : null}
      {tab === 'apps' ? <AppsTab open={open} onOpen={(id) => go('apps', id)} /> : null}
      {tab === 'requests' ? <RequestsTab open={open} onOpen={(id) => go('requests', id)} /> : null}
      {tab === 'log' ? <LogTab /> : null}
    </div>
  );
}
